import { useMemo, useState } from 'react';
import {
  SaveOutlined,
  UndoOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Scales24 } from '@carbon/icons-react';
import { QueueLayout } from 'components/pages/crm-spot/layouts/queue-layout';
import { ConfigurationProgress } from 'components/pages/crm-spot/shared-components';
import { Switch, Slider, Tooltip, Button } from 'antd';
import { useSettings } from 'components/pages/crm-spot/hooks/useSettings';
import { useTeamBalance } from 'components/pages/crm-spot/hooks/useTeamBalance';
import { TeamBalance } from 'components/pages/crm-spot/shared-components/team-balance';
import styles from './lead-balance.module.scss';

export const LeadBalance = () => {
  const [activeTab, setActiveTab] = useState('settings');

  const { settings, handleAppSetting } = useSettings();

  const configuredSettings = useMemo(() => {
    let configuredSettings = 0;
    if (settings['queue-automatic-balancing'] !== null) {
      configuredSettings++;
    }
    settings['queue-max-lead-gap'] && configuredSettings++;
    settings['queue-balance-frecuency'] && configuredSettings++;

    return configuredSettings;
  }, [settings]);

  const { teamBalance, fetchTeamBalance, loading } = useTeamBalance();

  const handleReset = () => {
    handleAppSetting('queue-automatic-balancing', null);
    handleAppSetting('queue-max-lead-gap', null);
    handleAppSetting('queue-balance-frecuency', null);
  };

  const handleSave = () => {
    // Save changes logic here
  };

  return (
    <QueueLayout
      title="Lead Balance"
      description="Lead balancing helps distribute workload evenly across your team. When enabled, the system automatically redistributes leads based on your settings to prevent any team member from being overwhelmed while others have few assignments."
      label="Configure automatic lead distribution"
      icon={<Scales24 />}
      buttons={[
        <Button icon={<UndoOutlined />} onClick={handleReset}>
          Reset to Default
        </Button>,
      ]}>
      <div className={styles.container}>
        {/* Configuration Progress */}
        <ConfigurationProgress
          title="Configuration Progress"
          icon={<SyncOutlined className={styles.progressIcon} />}
          configuredSettings={configuredSettings}
          totalSettings={3}
          isEnabled={settings['queue-automatic-balancing']}
          disabledMessage="Auto-balance disabled"
        />
        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'settings' ? styles.active : styles.inactive}`}
            onClick={() => setActiveTab('settings')}>
            <SettingOutlined />
            Balance Settings
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'distribution' ? styles.active : styles.inactive}`}
            onClick={() => setActiveTab('distribution')}>
            <BarChartOutlined />
            Team Distribution
          </button>
        </div>

        {activeTab === 'distribution' ? (
          <TeamBalance teamBalance={teamBalance} fetchTeamBalance={fetchTeamBalance} loading={loading} />
        ) : (
          <>
            {/* Automatic Lead Balancing Card */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <Scales24 className={styles.headerIcon} />
                  <div>
                    <h3 className={styles.headerTitle}>Automatic Lead Balancing</h3>
                    <p className={styles.description}>Periodically redistribute leads to maintain even workloads</p>
                  </div>
                </div>
                <Switch
                  checked={settings['queue-automatic-balancing']}
                  onChange={(value) => handleAppSetting('queue-automatic-balancing', value)}
                  style={{ backgroundColor: settings['queue-automatic-balancing'] ? '#00506C' : undefined }}
                />
              </div>
            </div>

            {/* Maximum Lead Gap Card */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <h3 className={styles.headerTitle}>
                    Maximum Lead Gap: {settings['queue-max-lead-gap']} leads
                    <Tooltip title="This is the maximum allowed difference in lead count between team members before the system triggers a rebalance.">
                      <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
                    </Tooltip>
                  </h3>
                </div>
              </div>
              <p className={styles.description}>
                Maximum difference in lead count between team members before auto-balancing
              </p>

              <div className={styles.slider}>
                <Slider
                  min={1}
                  max={20}
                  value={settings['queue-max-lead-gap']}
                  onChange={(value) => handleAppSetting('queue-max-lead-gap', value)}
                  styles={{
                    track: { backgroundColor: '#00506C' },
                    handle: { borderColor: '#00506C', backgroundColor: '#00506C' },
                  }}
                />
                <div className={styles.sliderLabels}>
                  <span className={styles.sliderLabel}>Small gap (more frequent balancing)</span>
                  <span className={styles.sliderLabel}>Large gap (less frequent balancing)</span>
                </div>
              </div>
            </div>

            {/* Balance Frequency Card */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <h3 className={styles.headerTitle}>
                    Balance Frequency: Every {settings['queue-balance-frecuency']} hour
                    {settings['queue-balance-frecuency'] > 1 ? 's' : ''}
                    <Tooltip title="Determines how often the system will check lead distribution and potentially rebalance if needed.">
                      <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
                    </Tooltip>
                  </h3>
                </div>
              </div>
              <p className={styles.description}>How often the system should check and rebalance leads</p>

              <div className={styles.slider}>
                <Slider
                  min={1}
                  max={24}
                  value={settings['queue-balance-frecuency']}
                  onChange={(value) => handleAppSetting('queue-balance-frecuency', value)}
                  styles={{
                    track: { backgroundColor: '#00506C' },
                    handle: { borderColor: '#00506C', backgroundColor: '#00506C' },
                  }}
                />
                <div className={styles.sliderLabels}>
                  <span className={styles.sliderLabel}>More frequent (30 mins)</span>
                  <span className={styles.sliderLabel}>Less frequent (24 hours)</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Configuration Summary Card - Outside of tabs */}
        <div className={styles.card}>
          <div className={styles.infoHeader}>
            <Scales24 className={styles.headerIcon} />
            <h3 className={styles.infoTitle}>Your Balance Configuration</h3>
          </div>
          <p className={styles.infoContent}>
            With your current settings, the system will check lead distribution every{' '}
            {settings['queue-balance-frecuency']} hour
            {settings['queue-balance-frecuency'] > 1 ? 's' : ''} and redistribute leads if any team member has{' '}
            {settings['queue-max-lead-gap']} or more leads than another team member.
          </p>
          {settings['queue-automatic-balancing'] ? (
            <div className={styles.statusBadge}>
              <CheckCircleOutlined /> Auto-Balance is Active
            </div>
          ) : null}
        </div>
      </div>
    </QueueLayout>
  );
};
